import { useNavigate } from 'react-router-dom';
import { Ref, RefObject, useEffect, useRef } from 'react';
import { DestinationSettings } from '../DestinationSettings/DestinationSettings';
import { WizardStepBaseProps } from '../../wizard/interfaces/wizard-step-base-props';
import { RawDestination } from '../../shared/interfaces/destination';
import { useAnalyticsTracker } from '../../tracking/hooks/useAnalyticsTracker';
import { DestinationTrackers } from '../tracking/destination-trackers';
import { toasterService } from '../../toaster/toaster.service';
import { DestinationAPI } from '../api/DestinationAPI';
import { EntityFormState } from '../../entity-form/interfaces/entity-form';
import { EntityFormImperativeRef } from '../../entity-form/EntityForm';
import { TestConnectionPropsFactory } from '../../entity-form/factories/testConnectionPropsFactory';
import { templateFormConnectionParamsFactory } from '../../template-form/factories/templateFormConnectionParamsFactory';
import { throwTemplateFormError } from '../../template-form/utils/templateFormErrorUtil';

export function CreateDestinationDestinationSettings({
  controller,
  updateWizardStepFooterProps
}: WizardStepBaseProps) {
  const destinationType = controller?.data.destinationType as string | null;

  const navigate = useNavigate();
  const { eventTrack } = useAnalyticsTracker();
  const destinationSettingsRef = useRef<EntityFormImperativeRef>();

  useEffect(() => {
    return () => {
      updateWizardStepFooterProps({});
    };
  }, []);

  const formDataResolver = () => {
    if (destinationType) {
      return DestinationAPI.getDestinationConfigTemplateByType(destinationType);
    }

    return Promise.reject(new Error('Failed to parse destination type'));
  };

  const defaultDestinationNameResolver = () => {
    if (destinationType) {
      return DestinationAPI.getDefaultDestinationName(destinationType);
    }

    return Promise.reject(new Error('Failed to parse destination type'));
  };

  const testConnectionResolver = (values: EntityFormState) => {
    if (destinationType) {
      const buildParams = templateFormConnectionParamsFactory(values, destinationType);
      return DestinationAPI.getTestConnectionTaskIdForNewDestination(destinationType, buildParams);
    }

    return throwTemplateFormError('Failed to resolve destination');
  };

  const formSubmitResolver = async (values: EntityFormState) => {
    const connectionParams = templateFormConnectionParamsFactory(values, destinationType!);
    return DestinationAPI.createDestination(connectionParams);
  };

  const formComplete = (rawDestination: RawDestination) => {
    eventTrack({
      action: DestinationTrackers.DESTINATION_TRACKER_CREATED_NEW_DESTINATION,
      properties: {
        destinationId: rawDestination.id,
        destinationType: rawDestination.destination_type
      }
    });

    toasterService.pop('success', `Successfully created ${rawDestination.name} destination`);

    controller!.getWizardController().markAsComplete();

    navigate(`../${rawDestination.id}/configuration`, { relative: 'path' });
  };

  return (
    <DestinationSettings
      isSBSDocsAllowed
      mode='CREATE'
      formDataResolver={formDataResolver}
      formSubmitResolver={formSubmitResolver}
      onComplete={values => formComplete(values as RawDestination)}
      formImperativeRef={destinationSettingsRef as unknown as Ref<EntityFormImperativeRef>}
      defaultDestinationNameResolver={defaultDestinationNameResolver}
      onStateChange={state => {
        const props = TestConnectionPropsFactory(
          state,
          destinationSettingsRef as RefObject<EntityFormImperativeRef>,
          'Test & Save'
        );
        updateWizardStepFooterProps(props);
      }}
      testConnectionResolver={testConnectionResolver}
    />
  );
}
